import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import * as Mount from "foldkit/mount";
import { defineView } from "foldkit/submodel";
import * as lucide from "lucide";

import type { ApiError } from "../../api/errors";
import type {
	Backlink,
	FolderCounts,
	LinkView,
	NoteDetail,
} from "../../api/schemas/notes";
import type { RecordSummary } from "../../api/schemas/records";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import { linkStateChip, relativeTime } from "../../widgets/a2aUi";
import {
	countsTotal,
	folderTreeView,
	treeFromCounts,
} from "../../widgets/folderTree";
import { icon } from "../../widgets/icons";
import * as NoteEditor from "../../widgets/noteEditor";
import {
	distinctLinks,
	headingLabel,
	linkCounts,
	linkLabel,
	plainSnippet,
} from "../../widgets/wikiLinks";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
import { editorId, hrefFor } from "./update";

type H = HtmlBuilder<Message>;
type Resolved = Extract<LinkView, { status: "resolved" }>;
type Ambiguous = Extract<LinkView, { status: "ambiguous" }>;

const glyph = (
	h: H,
	name:
		| "User"
		| "FileText"
		| "Split"
		| "Plus"
		| "Pencil"
		| "ArrowLeft"
		| "CornerDownRight"
		| "Users",
	size = 14,
) => icon(h, name, lucide[name], { size, class: "shrink-0 inline-block" });

const noteRoute = (id: string) => Route.Note({ id, asOf: Option.none() });

// ---- People ----------------------------------------------------------------

const ROLE_KEYS = ["role", "title", "jobTitle", "position"];
const COMPANY_KEYS = ["company", "organization", "employer"];
const pick = (record: RecordSummary, keys: ReadonlyArray<string>) =>
	keys.map((k) => record.attributes?.[k]).find((v) => v !== undefined && v);

/** "CTO at Acme", or whichever half exists, else the subtitle. */
export const personLine = (record: RecordSummary): string => {
	const role = pick(record, ROLE_KEYS);
	const company = pick(record, COMPANY_KEYS);
	return role && company
		? i18next.t("knowledge.note.roleAt", { role, company })
		: (role ?? company ?? record.subtitle ?? "");
};

// ---- Wiki links ------------------------------------------------------------

const resolvedLink = (
	h: H,
	shared: Shared,
	model: Model,
	link: Resolved,
): Html => {
	const label = `${linkLabel(link)}${headingLabel(link)}`;
	const record = model.records[link.id];
	const brief = record ? personLine(record) : "";
	const href = hrefFor(shared, link.id, link.type);
	return h.a(
		[
			...(href ? [h.Href(href)] : []),
			h.DataAttribute("testid", "wiki-resolved"),
			h.DataAttribute("link-type", link.type),
			// not-prose: the markdown's `prose a` rule would otherwise win (bold, base colour).
			h.Class(
				"not-prose link link-primary decoration-dotted underline-offset-2 hover:decoration-solid inline-flex items-baseline gap-0.5",
			),
			h.AriaLabel(
				i18next.t("knowledge.note.resolvedLabel", { label, name: link.name }),
			),
			...(brief ? [h.Title(`${link.name} · ${brief}`)] : []),
		],
		[
			link.type === "person" ? glyph(h, "User", 12) : h.empty,
			linkLabel(link),
			link.heading
				? h.span(
						[h.Class("text-base-content/60 text-[0.85em]")],
						[headingLabel(link)],
					)
				: h.empty,
		],
	);
};

const candidateLabel = (
	model: Model,
	link: Ambiguous,
	id: string,
	n: number,
) => {
	const record = model.records[id];
	if (!record)
		return i18next.t("knowledge.note.candidate", { name: link.target, n });
	const line = personLine(record);
	return line ? `${record.name} · ${line}` : record.name;
};

const ambiguousLink = (
	h: H,
	shared: Shared,
	model: Model,
	link: Ambiguous,
): Html => {
	const open = Option.getOrNull(model.chooser) === link.raw;
	const label = linkLabel(link);
	// On the trigger and the menu (focus is in one of them): Escape closes.
	const closeOnEscape = h.OnKeyDownPreventDefault((key) =>
		open && key === "Escape"
			? Option.some(Message.ToggledChooser({ raw: null }))
			: Option.none(),
	);
	return h.span(
		[h.Class("relative inline-block")],
		[
			open
				? h.button(
						[
							h.Type("button"),
							h.Tabindex(-1),
							h.AriaLabel(i18next.t("knowledge.note.closeChooser")),
							h.Class("fixed inset-0 z-10 cursor-default"),
							h.OnClick(Message.ToggledChooser({ raw: null })),
						],
						[],
					)
				: h.empty,
			h.button(
				[
					h.Type("button"),
					h.DataAttribute("testid", "wiki-ambiguous"),
					h.Class(
						"inline-flex items-baseline gap-1 rounded-sm px-1 bg-warning/20 underline decoration-dotted decoration-warning underline-offset-2 cursor-pointer",
					),
					h.AriaExpanded(open),
					closeOnEscape,
					h.AriaLabel(
						i18next.t("knowledge.note.ambiguousLabel", {
							label,
							count: link.ids.length,
						}),
					),
					h.OnClick(Message.ToggledChooser({ raw: open ? null : link.raw })),
				],
				[
					glyph(h, "Split", 12),
					`${label}${headingLabel(link)}`,
					h.span(
						[h.Class("text-[0.75em] font-semibold tabular-nums")],
						[`${link.ids.length}`],
					),
				],
			),
			open
				? h.ul(
						[
							h.DataAttribute("testid", "wiki-chooser"),
							closeOnEscape,
							h.AriaLabel(i18next.t("knowledge.note.chooserLabel", { label })),
							h.Class(
								"menu menu-sm not-prose absolute left-0 top-full z-20 mt-1 w-72 rounded-box border border-base-300 bg-base-100 shadow-lg",
							),
						],
						[
							h.li(
								[h.Class("menu-title")],
								[
									i18next.t("knowledge.note.chooserTitle", {
										count: link.ids.length,
									}),
								],
							),
							...link.ids.map((id, i) => {
								const record = model.records[id];
								const href = hrefFor(shared, id, record?.type);
								return h.li(
									[h.Key(id)],
									[
										h.a(href ? [h.Href(href)] : [], [
											record?.type === "person"
												? glyph(h, "User")
												: glyph(h, "FileText"),
											candidateLabel(model, link, id, i + 1),
										]),
									],
								);
							}),
						],
					)
				: h.empty,
		],
	);
};

const brokenLink = (h: H, shared: Shared, model: Model, link: LinkView) => {
	const label = `${linkLabel(link)}${headingLabel(link)}`;
	const style =
		"inline-flex items-baseline gap-0.5 text-error underline decoration-dashed underline-offset-2";
	if (Option.isSome(shared.asOf))
		return h.span(
			[
				h.DataAttribute("testid", "wiki-broken"),
				h.Role("img"),
				h.Class(style),
				h.AriaLabel(i18next.t("knowledge.note.brokenLabel", { label })),
			],
			[label],
		);
	const creating = Option.getOrNull(model.creating) === link.target;
	return h.button(
		[
			h.Type("button"),
			h.DataAttribute("testid", "wiki-broken"),
			h.Class(`${style} cursor-pointer`),
			h.Title(i18next.t("knowledge.note.createTitle", { target: link.target })),
			h.AriaLabel(
				i18next.t("knowledge.note.createLabel", { target: link.target }),
			),
			h.Disabled(Option.isSome(model.creating)),
			h.OnClick(Message.ClickedCreateLinked({ target: link.target })),
		],
		[label, creating ? h.empty : glyph(h, "Plus", 12)],
	);
};

// ---- Side panels -----------------------------------------------------------

const panel = (
	h: H,
	testId: string,
	title: string,
	count: number | null,
	children: ReadonlyArray<Html>,
): Html =>
	h.section(
		[
			h.DataAttribute("testid", testId),
			h.Class("card card-border bg-base-100 p-4 space-y-3"),
		],
		[
			h.h2(
				[
					h.Class(
						"flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-base-content/60",
					),
				],
				[
					title,
					count === null
						? h.empty
						: h.span(
								[
									h.DataAttribute("testid", `${testId}-count`),
									h.Class("badge badge-ghost badge-sm tabular-nums"),
								],
								[String(count)],
							),
				],
			),
			...children,
		],
	);

const muted = (h: H, text: string) =>
	h.p([h.Class("text-sm text-base-content/60")], [text]);

/** One row per distinct link; each state keeps its own control (anchor, chooser, create). */
const linksPanel = (
	h: H,
	shared: Shared,
	model: Model,
	links: ReadonlyArray<LinkView>,
): Html => {
	const distinct = distinctLinks(links);
	const counts = linkCounts(links);
	return panel(
		h,
		"note-links",
		i18next.t("knowledge.note.links"),
		distinct.length,
		distinct.length === 0
			? [muted(h, i18next.t("knowledge.note.noLinks"))]
			: [
					h.p(
						[
							h.DataAttribute("testid", "note-link-counts"),
							h.Class("text-sm text-base-content/70"),
						],
						[i18next.t("knowledge.note.linkCounts", counts)],
					),
					h.ul(
						[h.Class("space-y-1.5")],
						distinct.map((link) =>
							h.li(
								[
									h.Key(link.raw),
									h.Class("flex items-center justify-between gap-2 text-sm"),
								],
								[
									h.span(
										[h.Class("min-w-0")],
										[
											link.status === "resolved"
												? resolvedLink(h, shared, model, link)
												: link.status === "ambiguous"
													? ambiguousLink(h, shared, model, link)
													: brokenLink(h, shared, model, link),
										],
									),
									linkStateChip(h, link.status),
								],
							),
						),
					),
				],
	);
};

/** The snippet as text, with each `[[link]]` in it set off; never HTML. */
const snippetText = (h: H, snippet: string): ReadonlyArray<Html | string> =>
	snippet
		.split(/(\[\[[^[\]\n]+\]\])/)
		.map((part, i) =>
			i % 2 === 1
				? h.span([h.Class("font-medium text-base-content")], [part])
				: part,
		);

const backlinksPanel = (
	h: H,
	shared: Shared,
	backlinks: ReadonlyArray<Backlink>,
): Html =>
	panel(
		h,
		"note-backlinks",
		i18next.t("knowledge.note.backlinks"),
		backlinks.length,
		backlinks.length === 0
			? [muted(h, i18next.t("knowledge.note.noBacklinks"))]
			: [
					h.ul(
						[h.Class("space-y-3")],
						backlinks.map((b) =>
							h.li(
								[h.Key(b.id), h.DataAttribute("testid", "note-backlink")],
								[
									h.a(
										[
											h.Href(linkHref(shared.route, noteRoute(b.id))),
											h.Class(
												"link link-hover inline-flex items-baseline gap-1 font-medium text-sm",
											),
										],
										[
											glyph(h, "CornerDownRight", 12),
											h.span(
												[],
												[
													b.folder
														? h.span(
																[
																	h.DataAttribute(
																		"testid",
																		"note-backlink-folder",
																	),
																	h.Class("font-normal text-base-content/50"),
																],
																[`${b.folder}/`],
															)
														: h.empty,
													b.title || i18next.t("knowledge.list.untitled"),
												],
											),
										],
									),
									b.snippet
										? h.p(
												[
													h.Class(
														"mt-1 text-xs leading-relaxed text-base-content/70 line-clamp-3",
													),
												],
												snippetText(h, plainSnippet(b.snippet)),
											)
										: h.empty,
								],
							),
						),
					),
				],
	);

const peoplePanel = (
	h: H,
	shared: Shared,
	model: Model,
	links: ReadonlyArray<LinkView>,
): Html => {
	const people = distinctLinks(links).flatMap((link) =>
		link.status === "resolved" && link.type === "person" ? [link] : [],
	);
	const seen = [...new Map(people.map((p) => [p.id, p])).values()];
	return seen.length === 0
		? h.empty
		: panel(h, "note-people", i18next.t("knowledge.note.people"), seen.length, [
				h.ul(
					[h.Class("space-y-3")],
					seen.map((link) => {
						const record = model.records[link.id];
						const line = record ? personLine(record) : "";
						const href = hrefFor(shared, link.id, "person");
						return h.li(
							[
								h.Key(link.id),
								h.DataAttribute("testid", "note-person"),
								h.Class("flex items-start gap-2"),
							],
							[
								h.span(
									[
										h.Class(
											"mt-0.5 grid size-7 place-items-center rounded-full bg-primary/10 text-primary",
										),
									],
									[glyph(h, "User")],
								),
								h.div(
									[h.Class("min-w-0 text-sm")],
									[
										h.a(
											[
												...(href ? [h.Href(href)] : []),
												h.Class("link link-hover font-medium"),
											],
											[record?.name ?? link.name],
										),
										line
											? h.p([h.Class("text-base-content/70")], [line])
											: h.empty,
										record?.lastUpdated
											? h.p(
													[h.Class("text-xs text-base-content/50")],
													[
														i18next.t("knowledge.note.recordUpdated", {
															time: relativeTime(record.lastUpdated, model.now),
														}),
													],
												)
											: h.empty,
									],
								),
							],
						);
					}),
				),
			]);
};

// ---- Header, editor --------------------------------------------------------

const tagList = (h: H, tags: ReadonlyArray<string>): Html =>
	tags.length === 0
		? h.empty
		: h.ul(
				[h.Class("flex flex-wrap gap-1"), h.AriaLabel(i18next.t("entry.tags"))],
				tags.map((tag) =>
					h.li(
						[h.Key(tag), h.Class("badge badge-ghost badge-sm")],
						[`#${tag}`],
					),
				),
			);

/** "All notes / Projects / Atlas" above the title; each step opens that folder's list. */
const folderCrumbs = (h: H, shared: Shared, folder: string): Html => {
	const segments = folder.split("/").filter(Boolean);
	const crumb = (path: string | null, label: string) =>
		h.li(
			[h.Key(path ?? "__all")],
			[
				h.a(
					[
						h.Href(
							linkHref(
								shared.route,
								Route.Knowledge({
									asOf: Option.none(),
									folder: path === null ? Option.none() : Option.some(path),
								}),
							),
						),
						h.Class("text-base-content/60 hover:text-base-content"),
					],
					[label],
				),
			],
		);
	return h.nav(
		[
			h.DataAttribute("testid", "note-breadcrumb"),
			h.AriaLabel(i18next.t("knowledge.folders.breadcrumbLabel")),
			h.Class("breadcrumbs py-0 text-xs"),
		],
		[
			h.ul(
				[],
				[
					crumb(null, i18next.t("knowledge.folders.all")),
					...segments.map((name, i) =>
						crumb(segments.slice(0, i + 1).join("/"), name),
					),
				],
			),
		],
	);
};

/** Title (editable in place), breadcrumb, meta, and Save/Discard while there are unsaved changes. */
const header = (
	h: H,
	shared: Shared,
	model: Model,
	detail: NoteDetail,
): Html => {
	const readOnly = Option.isSome(shared.asOf);
	const draft = Option.getOrNull(model.draft);
	const title = draft?.title ?? detail.note.title;
	return h.header(
		[h.Class("space-y-2 border-b border-base-300 pb-4")],
		[
			folderCrumbs(h, shared, detail.note.folder ?? ""),
			h.h1(
				[h.Class("sr-only")],
				[detail.note.title || i18next.t("knowledge.list.untitled")],
			),
			h.input([
				h.Type("text"),
				h.DataAttribute("testid", "note-title"),
				h.Class(
					"w-full rounded-field bg-transparent px-1 -mx-1 font-display text-3xl font-bold leading-tight outline-none focus-visible:ring-2 focus-visible:ring-primary/40 placeholder:text-base-content/40",
				),
				h.Value(title),
				h.Placeholder(i18next.t("knowledge.list.untitled")),
				h.AriaLabel(i18next.t("knowledge.note.titleLabel")),
				h.Readonly(readOnly),
				h.AriaReadonly(readOnly),
				h.OnInput((value) => Message.ChangedTitle({ value })),
			]),
			h.div(
				[h.Class("flex flex-wrap items-center gap-x-3 gap-y-2 min-h-8")],
				[
					h.div(
						[
							h.Class(
								"flex flex-1 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-base-content/60",
							),
						],
						[
							detail.note.version
								? h.span(
										[h.DataAttribute("testid", "note-updated")],
										[
											i18next.t("knowledge.list.updated", {
												time: relativeTime(detail.note.version, model.now),
											}),
										],
									)
								: h.empty,
							tagList(h, detail.note.tags),
						],
					),
					readOnly || !draft
						? h.empty
						: h.div(
								[
									h.DataAttribute("testid", "note-save-bar"),
									h.Class("flex items-center gap-2"),
								],
								[
									h.span(
										[h.Role("status"), h.Class("text-xs text-base-content/60")],
										[i18next.t("knowledge.note.unsaved")],
									),
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-sm btn-ghost"),
											h.AriaLabel(i18next.t("knowledge.note.cancelLabel")),
											h.Disabled(model.saving),
											h.OnClick(Message.ClickedCancel()),
										],
										[i18next.t("knowledge.note.cancel")],
									),
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-sm btn-primary"),
											h.AriaLabel(i18next.t("knowledge.note.saveLabel")),
											h.Title(i18next.t("knowledge.note.saveShortcut")),
											h.Disabled(
												model.saving ||
													model.conflict ||
													draft.title.trim() === "",
											),
											h.OnClick(Message.ClickedSave()),
										],
										[
											model.saving
												? i18next.t("knowledge.note.saving")
												: i18next.t("knowledge.note.save"),
										],
									),
								],
							),
				],
			),
		],
	);
};

/** The one editing surface: markdown that renders as you type (read-only while time travelling). */
const editorSurface = (
	h: H,
	shared: Shared,
	model: Model,
	detail: NoteDetail,
): Html => {
	const readOnly = Option.isSome(shared.asOf);
	const id = editorId(model.id);
	const hintId = `${id}-hint`;
	const draft = Option.getOrNull(model.draft);
	return h.div(
		[h.Class("space-y-2")],
		[
			h.div([
				h.DataAttribute("testid", "note-editor"),
				// A new key remounts: read-only is frozen at mount.
				h.Key(`${id}-${readOnly ? "ro" : "rw"}`),
				h.Class(
					"rounded-box -mx-2 px-2 focus-within:ring-2 focus-within:ring-primary/30",
				),
				h.OnMount(
					Mount.mapMessage(
						NoteEditor.NoteEditor({
							id,
							initialText: draft?.body ?? detail.note.body,
							title: detail.note.title,
							tags: detail.note.tags,
							links: NoteEditor.editorLinks(detail.links),
							readOnly,
							label: i18next.t("knowledge.note.bodyLabel"),
							placeholder: i18next.t("knowledge.note.bodyPlaceholder"),
							describedBy: hintId,
						}),
						(message) => Message.GotNoteEditorMessage({ message }),
					),
				),
			]),
			h.p(
				[
					h.Id(hintId),
					h.DataAttribute("testid", "note-editor-hint"),
					h.Class("text-xs text-base-content/50"),
				],
				[
					i18next.t(
						readOnly
							? "knowledge.note.editorHintReadOnly"
							: "knowledge.note.editorHint",
					),
				],
			),
		],
	);
};

const conflictAlert = (h: H): Html =>
	h.div(
		[
			h.DataAttribute("testid", "note-conflict"),
			h.Role("alert"),
			h.Class("alert alert-warning alert-soft"),
		],
		[
			h.span([], [i18next.t("knowledge.note.conflict")]),
			h.button(
				[
					h.Type("button"),
					h.Class("btn btn-sm"),
					h.AriaLabel(i18next.t("knowledge.note.reloadLabel")),
					h.OnClick(Message.ClickedReload()),
				],
				[i18next.t("knowledge.note.reload")],
			),
		],
	);

/** The whole folder tree beside the note, its folder selected (wide screens; the breadcrumb covers narrow ones). */
const folderColumn = (
	h: H,
	shared: Shared,
	model: Model,
	folder: string,
	counts: FolderCounts,
): Html =>
	h.nav(
		[
			h.DataAttribute("testid", "note-folder-tree"),
			h.AriaLabel(i18next.t("knowledge.folders.treeLabel")),
			h.Class("hidden xl:block card card-border bg-base-100 p-2"),
		],
		[
			folderTreeView(h, {
				// The tree counts notes per path, so each note is one entry.
				nodes: treeFromCounts(counts),
				allCount: countsTotal(counts),
				selected: folder === "" ? null : folder,
				toggled: model.toggled,
				href: (path) =>
					linkHref(
						shared.route,
						Route.Knowledge({
							asOf: Option.none(),
							folder: path === null ? Option.none() : Option.some(path),
						}),
					),
				onToggle: (path) => Message.ToggledFolder({ path }),
				drop: undefined,
			}),
		],
	);

const loaded = (
	h: H,
	shared: Shared,
	model: Model,
	detail: NoteDetail,
): Html => {
	const counts = Option.getOrNull(AsyncData.getData(model.folders));
	return h.div(
		[
			h.Class(
				`grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] items-start ${counts ? "xl:grid-cols-[13rem_minmax(0,1fr)_18rem]" : ""}`,
			),
		],
		[
			counts
				? folderColumn(h, shared, model, detail.note.folder ?? "", counts)
				: h.empty,
			h.article(
				[
					h.DataAttribute("testid", "note-article"),
					h.Class("card card-border bg-base-100 p-6 space-y-4 min-w-0"),
				],
				[
					header(h, shared, model, detail),
					editorSurface(h, shared, model, detail),
				],
			),
			h.aside(
				[
					h.Class("space-y-4"),
					h.AriaLabel(i18next.t("knowledge.note.sidebar")),
				],
				[
					backlinksPanel(h, shared, detail.backlinks),
					linksPanel(h, shared, model, detail.links),
					peoplePanel(h, shared, model, detail.links),
				],
			),
		],
	);
};

const status = (h: H, text: string, testId: string, error = false): Html =>
	h.p(
		[
			h.DataAttribute("testid", testId),
			h.Role(error ? "alert" : "status"),
			h.Class(error ? "text-error text-sm" : "text-base-content/60 text-sm"),
		],
		[text],
	);

const failure = (h: H, error: ApiError) =>
	error._tag === "NotFound"
		? status(h, i18next.t("knowledge.note.notFound"), "note-not-found", true)
		: status(h, i18next.t("knowledge.note.loadError"), "note-error", true);

export const view = defineView<Model, Message, Shared>((model, shared, h) =>
	h.section(
		[
			h.DataAttribute("testid", "page-Note"),
			h.Class("mx-auto w-full max-w-7xl space-y-4"),
		],
		[
			h.a(
				[
					h.Href(
						linkHref(shared.route, Route.Knowledge({ asOf: Option.none() })),
					),
					h.Class(
						"link link-hover inline-flex items-center gap-1 text-sm text-base-content/70",
					),
				],
				[glyph(h, "ArrowLeft"), i18next.t("knowledge.title")],
			),
			Option.isSome(shared.asOf)
				? h.p(
						[
							h.DataAttribute("testid", "note-live"),
							h.Class("text-sm text-base-content/60"),
						],
						[i18next.t("knowledge.note.liveData")],
					)
				: h.empty,
			model.conflict ? conflictAlert(h) : h.empty,
			Option.match(model.error, {
				onNone: () => h.empty,
				onSome: (error) => status(h, error, "note-action-error", true),
			}),
			AsyncData.match(model.note, {
				onIdle: () => h.empty,
				onLoading: () =>
					status(h, i18next.t("knowledge.note.loading"), "note-loading"),
				onFailure: (error) => failure(h, error),
				onSuccess: (detail) => loaded(h, shared, model, detail),
				onRefreshing: (detail) => loaded(h, shared, model, detail),
				onStale: ({ data }) => loaded(h, shared, model, data),
			}),
		],
	),
);
